"use client"

import { ChevronUp, KeyRound, LogIn, LogOut, Luggage, Map, MapPinHouse, MessageCircleQuestion, Plane, TowerControl, UserRound, UserRoundCog, PanelLeftOpen, PanelLeftClose, Moon, Sun, TicketPlus } from "lucide-react"
import { SignedIn, SignedOut, SignOutButton, useClerk, useUser } from '@clerk/nextjs'
import Link from 'next/link'
import Image from 'next/image'
import { usePathname } from 'next/navigation'
import { useState } from 'react'
import { useTheme } from "next-themes"

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar"

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { ProfileDialog } from "@/app/profile/profile-dialog"
import { FlightAddDialog } from "@/app/flights/flight-add-dialog"
import { GET_USER_DISTINCT_COUNTS } from '@/lib/graphql/queries';
import { useQuery } from '@apollo/client';

// Menu items.
const items = [
  {
    title: "Home",
    url: "/",
    icon: MapPinHouse,
  },
  {
    title: "Flights",
    url: "/flights",
    icon: Plane,
  },
  {
    title: "Airlines",
    url: "/airlines",
    icon: Luggage,
  },
  {
    title: "Airports",
    url: "/airports",
    icon: TowerControl,
  },
  {
    title: "Maps",
    url: "/maps",
    icon: Map,
  },
]

// Custom theme toggle that matches sidebar button styling
function SidebarModeToggle() {
  const { setTheme } = useTheme()

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <SidebarMenuButton size="sm" className="h-6 w-6">
          <Sun className="h-4 w-4 scale-100 rotate-0 transition-all dark:scale-0 dark:-rotate-90" />
          <Moon className="absolute h-4 w-4 scale-0 rotate-90 transition-all dark:scale-100 dark:rotate-0" />
          <span className="sr-only">Toggle theme</span>
        </SidebarMenuButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => setTheme("light")}>
          Light
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => setTheme("dark")}>
          Dark
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => setTheme("system")}>
          System
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function UserDropdown({ isCollapsed = false }: { isCollapsed?: boolean }) {
  const { openUserProfile } = useClerk()
  const { user, isLoaded } = useUser()
  const [profileDialogOpen, setProfileDialogOpen] = useState(false)

  // Get user initials for fallback
  const getInitials = (firstName?: string | null, lastName?: string | null, email?: string) => {
    if (firstName && lastName) {
      return `${firstName[0]}${lastName[0]}`.toUpperCase()
    }
    if (firstName) {
      return firstName[0].toUpperCase()
    }
    if (email) {
      return email[0].toUpperCase()
    }
    return 'U'
  }

  // Don't render anything if user data isn't loaded yet
  if (!isLoaded) {
    return (
      <SidebarMenuButton disabled>
        <Avatar className="size-4">
          <AvatarFallback>
            <UserRound className="size-4" />
          </AvatarFallback>
        </Avatar>
        <span className="truncate">Loading...</span>
        <ChevronUp className="ml-auto size-4" />
      </SidebarMenuButton>
    )
  }

  // If loaded but no user (shouldn't happen in SignedIn context, but just in case)
  if (!user) {
    return (
      <SidebarMenuButton disabled>
        <Avatar className="size-4">
          <AvatarFallback>
            <UserRound className="size-4" />
          </AvatarFallback>
        </Avatar>
        <span className="truncate">User</span>
        <ChevronUp className="ml-auto size-4" />
      </SidebarMenuButton>
    )
  }

  const userInitials = getInitials(
    user.firstName,
    user.lastName,
    user.emailAddresses?.[0]?.emailAddress
  )

  const menuButton = (
    <SidebarMenuButton className="cursor-pointer">
      <Avatar className="size-4">
        <AvatarImage src={user.imageUrl} alt={user.firstName || 'User'} />
        <AvatarFallback>
          {userInitials}
        </AvatarFallback>
      </Avatar>
      <span className="truncate">
        {user.firstName || user.emailAddresses?.[0]?.emailAddress || 'User'}
      </span>
      <ChevronUp className="ml-auto size-4" />
    </SidebarMenuButton>
  )

  return (
    <>
      <DropdownMenu>
        {isCollapsed ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <DropdownMenuTrigger asChild>
                {menuButton}
              </DropdownMenuTrigger>
            </TooltipTrigger>
            <TooltipContent side="right">
              Profile
            </TooltipContent>
          </Tooltip>
        ) : (
          <DropdownMenuTrigger asChild>
            {menuButton}
          </DropdownMenuTrigger>
        )}
        <DropdownMenuContent
          side="top"
          className="w-[--radix-popper-anchor-width]"
        >
          <DropdownMenuItem onClick={() => setProfileDialogOpen(true)}>
            <UserRoundCog className="size-4" />
            <span>Profile</span>
          </DropdownMenuItem>
          <DropdownMenuItem 
            onClick={() => openUserProfile()}
          >
            <KeyRound className="size-4" />
            <span>Manage login</span>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <SignOutButton>
              <button className="flex items-center w-full">
                <LogOut className="size-4" />
                <span>Sign out</span>
              </button>
            </SignOutButton>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      
      <ProfileDialog 
        open={profileDialogOpen} 
        onOpenChange={setProfileDialogOpen} 
      />
    </>
  )
}

function SidebarHeader() {
  const { state } = useSidebar()
  const [isHovering, setIsHovering] = useState(false)
  
  const isCollapsed = state === 'collapsed'

  return (
    <div className="relative flex items-center justify-between h-8 px-2 mb-4">
      {/* Logo area with hover behavior when collapsed */}
      <div 
        className={`flex items-center gap-2 ${isCollapsed ? 'cursor-pointer w-full justify-center' : ''}`}
        onMouseEnter={() => isCollapsed && setIsHovering(true)}
        onMouseLeave={() => isCollapsed && setIsHovering(false)}
      >
        {isCollapsed && isHovering ? (
          <SidebarMenuButton size="sm" className="h-6 w-6" asChild>
            <SidebarTrigger>
              <PanelLeftOpen className="size-4" />
            </SidebarTrigger>
          </SidebarMenuButton>
        ) : (
          <>
            <div className="relative flex-shrink-0 group-data-[collapsible=icon]:size-4 size-6 transition-all duration-300 ease-in-out">
              <Image
                src="/favicon.svg"
                alt="Taya Flights"
                fill
                className="dark:hidden object-contain"
              />
              <Image
                src="/favicon.svg"
                alt="Taya Flights"
                fill
                className="hidden dark:block object-contain"
              />
            </div>
            <span className="group-data-[collapsible=icon]:hidden text-lg">
              Taya
            </span>
          </>
        )}
      </div>
      
      {/* Controls when expanded - theme toggle and collapse button */}
      {!isCollapsed && (
        <div className="flex items-center gap-1">
          <SidebarModeToggle />
          <SidebarMenuButton size="sm" className="h-6 w-6" asChild>
            <SidebarTrigger>
              <PanelLeftClose className="size-4" />
            </SidebarTrigger>
          </SidebarMenuButton>
        </div>
      )}
    </div>
  )
}

export function AppSidebar() {
  const pathname = usePathname();
  const { state } = useSidebar();
  const isCollapsed = state === 'collapsed';
  const [addFlightDialogOpen, setAddFlightDialogOpen] = useState(false);

  // Fetch all three counts in one query
  const { data, loading, error } = useQuery(GET_USER_DISTINCT_COUNTS, {
    variables: { includeFutureFlights: true },
    fetchPolicy: 'cache-first',
  });

  // Helper to render badge for a menu item
  function renderSidebarBadge(itemTitle: string) {
    if (loading || error || !data) return null;
    const counts = data.userDistinctCounts;
    if (!counts) return null;
    let value = null;
    if (itemTitle === 'Flights') value = counts.flightCount;
    if (itemTitle === 'Airlines') value = counts.airlineCount;
    if (itemTitle === 'Airports') value = counts.airportCount;
    if (!value || value === 0) return null;
    const isActive =
      (itemTitle === 'Flights' && pathname === '/flights') ||
      (itemTitle === 'Airlines' && pathname === '/airlines') ||
      (itemTitle === 'Airports' && pathname === '/airports');
    return (
      <SidebarMenuBadge>
        <span className={isActive ? '!text-sidebar-primary-foreground' : ''}>{value}</span>
      </SidebarMenuBadge>
    );
  }

  return (
    <>
      <Sidebar collapsible="icon">
        <SidebarContent>
          <SidebarGroup>
            <SidebarHeader />
            <SidebarGroupContent>
              <SidebarMenu>
                {/* Add Flight Button */}
                <SidebarMenuItem>
                  {isCollapsed ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <SidebarMenuButton onClick={() => setAddFlightDialogOpen(true)}>
                          <TicketPlus className="size-4" />
                          <span>Add Flight</span>
                        </SidebarMenuButton>
                      </TooltipTrigger>
                      <TooltipContent side="right">
                        Add Flight
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    <SidebarMenuButton onClick={() => setAddFlightDialogOpen(true)}>
                      <TicketPlus className="size-4" />
                      <span>Add Flight</span>
                    </SidebarMenuButton>
                  )}
                </SidebarMenuItem>
                
                {/* Spacing */}
                <div className="h-2" />
                
                {items.map((item) => {
                  const menuButton = (
                    <SidebarMenuButton 
                      asChild 
                      isActive={pathname === item.url}
                      className={pathname === item.url ? "!bg-sidebar-primary !text-sidebar-primary-foreground !font-semibold hover:!bg-sidebar-primary hover:!text-sidebar-primary-foreground" : ""}
                    >
                      <Link href={item.url}>
                        <item.icon className="size-4" />
                        <span>{item.title}</span>
                      </Link>
                    </SidebarMenuButton>
                  )

                  return (
                    <SidebarMenuItem key={item.title}>
                      {isCollapsed ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            {menuButton}
                          </TooltipTrigger>
                          <TooltipContent side="right">
                            {item.title}
                          </TooltipContent>
                        </Tooltip>
                      ) : (
                        <>
                          {menuButton}
                          {renderSidebarBadge(item.title)}
                        </>
                      )}
                    </SidebarMenuItem>
                  )
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>
        <SidebarFooter>
          <SidebarMenu>
            <SidebarMenuItem>
              {isCollapsed ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <SidebarMenuButton 
                      asChild 
                      isActive={pathname === "/about"}
                      className={pathname === "/about" ? "!bg-sidebar-primary !text-sidebar-primary-foreground !font-semibold hover:!bg-sidebar-primary hover:!text-sidebar-primary-foreground" : ""}
                    >
                      <Link href="/about">
                        <MessageCircleQuestion className="size-4" />
                        <span>About</span>
                      </Link>
                    </SidebarMenuButton>
                  </TooltipTrigger>
                  <TooltipContent side="right">
                    About
                  </TooltipContent>
                </Tooltip>
              ) : (
                <SidebarMenuButton 
                  asChild 
                  isActive={pathname === "/about"}
                  className={pathname === "/about" ? "!bg-sidebar-primary !text-sidebar-primary-foreground !font-semibold hover:!bg-sidebar-primary hover:!text-sidebar-primary-foreground" : ""}
                >
                  <Link href="/about">
                    <MessageCircleQuestion className="size-4" />
                    <span>About</span>
                  </Link>
                </SidebarMenuButton>
              )}
            </SidebarMenuItem>
            <SidebarMenuItem>
              <SignedIn>
                <UserDropdown isCollapsed={isCollapsed} />
              </SignedIn>
              <SignedOut>
                {isCollapsed ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <SidebarMenuButton asChild>
                        <Link href="/login">
                          <LogIn className="size-4" />
                          <span>Sign in</span>
                        </Link>
                      </SidebarMenuButton>
                    </TooltipTrigger>
                    <TooltipContent side="right">
                      Sign in
                    </TooltipContent>
                  </Tooltip>
                ) : (
                  <SidebarMenuButton asChild>
                    <Link href="/login">
                      <LogIn className="size-4" />
                      <span>Sign in</span>
                    </Link>
                  </SidebarMenuButton>
                )}
              </SignedOut>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarFooter>
      </Sidebar>
      
      <FlightAddDialog 
        open={addFlightDialogOpen} 
        onOpenChange={setAddFlightDialogOpen} 
      />
    </>
  )
}